import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../../../components/AppIcon';
import Button from '../../../../components/ui/Button';
import { motion } from 'framer-motion';
import { getFullUrl } from '../../../../utils/url';
import { appDate } from '../../../../utils/appDateFormat';

const defaultProfile = '/images/defaultProfile.png';

interface Reflection {
  id: number;
  promptId: string;
  promptTitle: string;
  category: string;
  icon: string;
  response: string;
}

interface ManagerResponse {
  message: string;
  respondedAt: string;
}

interface Member {
  employeeId: string;
  employeeName: string;
  employeeAvatar?: string;
  position: string;
  department: string;
  reflectionMonth: string;
  submittedDate?: string;
  status: string;
  totalPrompts: number;
  completedPrompts: number;
  hasResponse: boolean;
  managerResponse?: ManagerResponse;
  reflections?: Reflection[];
}

interface ReflectionDetailPanelProps {
  member?: Member;
  onResponseSubmit: (employeeId: string, response: string) => Promise<void> | void;
}

const ReflectionDetailPanel: React.FC<ReflectionDetailPanelProps> = ({ 
  member, 
  onResponseSubmit 
}) => {
  const { t, i18n } = useTranslation();
  const [responseText, setResponseText] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const maxChars = 2000;
  const minChars = 50;

  // Reset response text when member changes
  useEffect(() => {
    setResponseText(member?.managerResponse?.message || '');
  }, [member?.employeeId, member?.managerResponse?.message]);

  const handleResponseChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    if (value.length <= maxChars) {
      setResponseText(value);
    }
  };

  const handleSubmitResponse = async () => {
    if (responseText.length >= minChars && member) {
      setIsSubmitting(true);
      try {
        await onResponseSubmit(member.employeeId, responseText);
        // Show success message
      } catch (error) {
        console.error('Failed to submit response:', error);
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const getInitials = (name?: string) => {
    if (!name) return '';
    return name.split(' ').map(word => word.charAt(0)).join('').toUpperCase().slice(0, 2);
  };

  const getAvatarUrl = (avatarUrl?: string): string => {
    return getFullUrl(avatarUrl) || defaultProfile;
  };

  const getReflectionIcon = (icon: string): string => {
    const iconMap: { [key: string]: string } = {
      'Trophy': 'EmojiEvents',
      'Heart': 'People', 
      'TrendingUp': 'TrendingUp',
      'Shield': 'Shield'
    };
    return iconMap[icon] || icon;
  };

  const getIconColor = (icon: string): string => {
    const colorMap: { [key: string]: string } = {
      'EmojiEvents': '#f59e0b',
      'People': '#2563eb',
      'TrendingUp': '#10b981',
      'Shield': '#8b5cf6'
    };
    return colorMap[icon] || '#6366f1';
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return t('performance.managerResponse.notAvailable');
    return appDate(dateString);
  };

  const canSubmit = responseText.length >= minChars && !member?.hasResponse;

  if (!member) {
    return (
      <div className="bg-card border border-border rounded-lg p-12 text-center">
        <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
          <Icon name="UserCheck" size={32} className="text-muted-foreground" />
        </div>
        <h3 className="text-lg font-semibold text-foreground mb-2">
          {t('performance.managerResponse.selectTeamMember')}
        </h3>
        <p className="text-muted-foreground">
          {t('performance.managerResponse.selectTeamMemberDesc')}
        </p>
      </div>
    );
  }

  if (member.status === 'not_submitted') {
    return (
      <div className="bg-card border border-border rounded-lg p-12 text-center">
        <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
          <Icon name="AlertCircle" size={32} className="text-muted-foreground" />
        </div>
        <h3 className="text-lg font-semibold text-foreground mb-2">
          {t('performance.managerResponse.reflectionNotSubmitted')}
        </h3>
        <p className="text-muted-foreground">
          {t('performance.managerResponse.reflectionNotSubmittedDesc', { name: member.employeeName, month: member.reflectionMonth })}
        </p>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      {/* Employee Header */}
      <div className="bg-gradient-to-r from-pink-50 to-rose-50 p-6 border-b border-border">
        <div className="flex items-start gap-4">
          <img
            src={getAvatarUrl(member.employeeAvatar)}
            alt={member.employeeName}
            className="w-16 h-16 rounded-full object-cover"
            onError={(e) => {
              e.currentTarget.src = defaultProfile;
            }}
          />
          <div className="flex-1">
            <h2 className="text-xl font-bold text-foreground">{member.employeeName}</h2>
            <p className="text-muted-foreground">{member.position}</p>
            <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
              <div className="flex items-center gap-1">
                <Icon name="Building2" size={14} />
                <span>{member.department}</span>
              </div>
              <div className="flex items-center gap-1">
                <Icon name="Calendar" size={14} />
                <span>{member.reflectionMonth}</span>
              </div>
            </div>
          </div>
          <div className="text-end">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-card rounded-full border border-border">
              <Icon name="CheckCircle" size={16} className="text-green-600" />
              <span className="text-sm font-medium text-foreground">
                {t('performance.managerResponse.completionCount', { completed: member.completedPrompts, total: member.totalPrompts })}
              </span>
            </div>
          </div>
        </div>
      </div>
      {/* Reflections Content */}
      <div className="p-6 space-y-6 max-h-[calc(100vh-500px)] overflow-y-auto">
        {(member.reflections || []).map((reflection, index) => (
          <motion.div
            key={reflection.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: index * 0.1 }}
            className="border border-border rounded-lg p-4"
          >
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: getIconColor(getReflectionIcon(reflection.icon)) + '20' }}>
                  <Icon name={getReflectionIcon(reflection.icon) as any} size={20} style={{ color: getIconColor(getReflectionIcon(reflection.icon)) }} strokeWidth={2} />
                </div>
                <div>
                  <h3 className="text-base font-semibold text-foreground">
                    {reflection.promptTitle}
                  </h3>
                  <p className="text-xs text-muted-foreground">
                    {reflection.category}
                  </p>
                </div>
              </div>
            </div>
            
            <div className="mt-3">
              <p className="text-foreground leading-relaxed whitespace-pre-wrap">
                {reflection.response}
              </p>
            </div>
          </motion.div>
        ))}

        {(!member.reflections || member.reflections.length === 0) && (
          <div className="text-center py-8">
            <Icon name="FileText" size={48} className="text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">{t('performance.managerResponse.noReflections')}</p>
          </div>
        )}
      </div>
      {/* Manager Response Section */}
      <div className="border-t border-border p-6 bg-muted/30">
        <div className="flex items-center gap-2 mb-4">
          <Icon name="MessageSquare" size={20} className="text-primary" />
          <h3 className="text-lg font-semibold text-foreground">
            {member.hasResponse ? t('performance.managerResponse.yourResponse') : t('performance.managerResponse.managerResponse')}
          </h3>
        </div>

        {member.hasResponse ? (
          <div className="bg-card border border-border rounded-lg p-4">
            <p className="text-foreground leading-relaxed mb-3">{member.managerResponse?.message}</p>
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Icon name="Clock" size={12} />
              <span>{t('performance.managerResponse.respondedOn', { date: formatDate(member.managerResponse?.respondedAt) })}</span>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Response Textarea */}
            <div className="relative">
              <textarea
                value={responseText}
                onChange={handleResponseChange}
                placeholder={t('performance.managerResponse.responsePlaceholder')}
                className="w-full min-h-32 p-4 border border-border rounded-lg resize-none focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary transition-gentle bg-card text-foreground"
                rows={6}
                disabled={member.hasResponse}
              />
            </div>

            {/* Character Count and Actions */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <span className={`text-sm font-medium ${
                  responseText.length < minChars
                    ? 'text-muted-foreground'
                    : responseText.length > maxChars * 0.9
                    ? 'text-warning' :'text-success'
                }`}>
                  {responseText.length}/{maxChars}
                </span>
                {responseText.length < minChars && !member.hasResponse && (
                  <span className="text-xs text-muted-foreground">
                    {t('performance.managerResponse.minCharsRequired', { count: minChars })}
                  </span>
                )}
              </div>

              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setResponseText('')}
                  disabled={!responseText || member.hasResponse}
                  iconName="X"
                  iconPosition="left"
                >
                  {t('performance.managerResponse.clear')}
                </Button>
                <Button
                  variant="default"
                  size="sm"
                  onClick={handleSubmitResponse}
                  disabled={!canSubmit || isSubmitting || member.hasResponse}
                  iconName={isSubmitting ? 'Loader2' : 'Send'}
                  iconPosition="left"
                >
                  {isSubmitting ? t('performance.managerResponse.sending') : t('performance.managerResponse.sendResponse')}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReflectionDetailPanel;